import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Truck, Save, ArrowDown, ArrowUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyRule = () => ({
  rule_id: '',
  name: '',
  min_amount: 0,
  max_amount: null,
  courier_id: '',
  courier_name: '',
  priority: 100,
  active: true,
});

const AdminCourierRules = () => {
  const [rules, setRules] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingCouriers, setLoadingCouriers] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchCouriers();
  }, []);

  const fetchRules = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/courier-rules`, { withCredentials: true });
      setRules(data.rules || []);
    } catch {
      toast.error('Failed to load courier rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCouriers = async () => {
    setLoadingCouriers(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/couriers`, { withCredentials: true });
      setCouriers(data.couriers || []);
      if (data.demo_mode) {
        toast.info('Showing demo courier list — configure Shiprocket for live couriers.');
      }
    } catch {
      toast.error('Failed to load couriers');
    } finally {
      setLoadingCouriers(false);
    }
  };

  const addRule = () => setRules([...rules, emptyRule()]);

  const updateRule = (idx, field, value) => {
    const next = [...rules];
    next[idx] = { ...next[idx], [field]: value };
    // auto-fill courier_name when selecting courier_id
    if (field === 'courier_id') {
      const c = couriers.find((x) => String(x.courier_company_id) === String(value));
      if (c) next[idx].courier_name = c.courier_name;
    }
    setRules(next);
  };

  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));

  const movePriority = (idx, delta) => {
    const next = [...rules];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    // renumber priority 10, 20, 30...
    next.forEach((r, i) => { r.priority = (i + 1) * 10; });
    setRules(next);
  };

  const saveRules = async () => {
    // validate
    for (const r of rules) {
      if (!r.name || !r.courier_id) {
        toast.error('Every rule needs a name and courier');
        return;
      }
      if (r.min_amount !== null && r.max_amount !== null && r.max_amount !== '' && Number(r.max_amount) < Number(r.min_amount)) {
        toast.error(`Rule "${r.name}": max amount must be >= min amount`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        rules: rules.map((r) => ({
          rule_id: r.rule_id || undefined,
          name: r.name,
          min_amount: Number(r.min_amount || 0),
          max_amount: r.max_amount === '' || r.max_amount === null ? null : Number(r.max_amount),
          courier_id: Number(r.courier_id),
          courier_name: r.courier_name || '',
          priority: Number(r.priority || 100),
          active: !!r.active,
        })),
      };
      const { data } = await axios.put(`${API}/api/admin/courier-rules`, payload, { withCredentials: true });
      setRules(data.rules || []);
      toast.success('Courier rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16 max-w-5xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="courier-rules-heading">
              <Truck className="h-7 w-7 text-primary" /> Courier Preference Rules
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Orders are matched top-to-bottom. First active rule whose amount range includes the order total wins.
              A per-order override on the order detail page always takes precedence.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchCouriers} disabled={loadingCouriers} data-testid="refresh-couriers-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingCouriers ? 'animate-spin' : ''}`} /> Refresh Couriers
            </Button>
            <Button size="sm" onClick={saveRules} disabled={saving} data-testid="save-rules-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Rules'}
            </Button>
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-white border border-[#E0E0E0]">
          {rules.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-muted-foreground mb-4" data-testid="no-rules-message">
                No rules configured. Shiprocket will assign its default courier.
              </p>
              <Button onClick={addRule} data-testid="add-first-rule-btn">
                <Plus className="h-4 w-4 mr-1" /> Add First Rule
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {rules.map((rule, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-[#E0E0E0] bg-[#FAFAFA]" data-testid={`rule-row-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">
                        Priority {rule.priority || (idx + 1) * 10}
                      </span>
                      <Switch
                        checked={!!rule.active}
                        onCheckedChange={(v) => updateRule(idx, 'active', v)}
                        data-testid={`rule-active-${idx}`}
                      />
                      <span className="text-xs text-muted-foreground">{rule.active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => movePriority(idx, -1)} disabled={idx === 0} data-testid={`move-up-${idx}`}>
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => movePriority(idx, 1)} disabled={idx === rules.length - 1} data-testid={`move-down-${idx}`}>
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => removeRule(idx)} data-testid={`remove-rule-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Rule name</Label>
                      <Input
                        value={rule.name}
                        onChange={(e) => updateRule(idx, 'name', e.target.value)}
                        placeholder="e.g. Small orders"
                        data-testid={`rule-name-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Min amount (₹)</Label>
                      <Input
                        type="number"
                        value={rule.min_amount ?? 0}
                        onChange={(e) => updateRule(idx, 'min_amount', e.target.value)}
                        placeholder="0"
                        data-testid={`rule-min-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max amount (₹, blank = no cap)</Label>
                      <Input
                        type="number"
                        value={rule.max_amount ?? ''}
                        onChange={(e) => updateRule(idx, 'max_amount', e.target.value)}
                        placeholder="no cap"
                        data-testid={`rule-max-${idx}`}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-xs">Courier</Label>
                      <select
                        value={rule.courier_id || ''}
                        onChange={(e) => updateRule(idx, 'courier_id', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid={`rule-courier-${idx}`}
                      >
                        <option value="">Select courier...</option>
                        {couriers.map((c) => (
                          <option key={c.courier_company_id} value={c.courier_company_id}>
                            {c.courier_name} (id {c.courier_company_id}){c.rate ? ` — ₹${c.rate}` : ''}
                          </option>
                        ))}
                        {rule.courier_id && !couriers.find((c) => String(c.courier_company_id) === String(rule.courier_id)) && (
                          <option value={rule.courier_id}>
                            {rule.courier_name || 'Courier'} (id {rule.courier_id})
                          </option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addRule} className="w-full rounded-full" data-testid="add-rule-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Another Rule
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 rounded-xl bg-secondary/5 border border-secondary/20 text-xs text-muted-foreground">
          <p className="font-semibold text-secondary mb-1">How resolution works</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>If the order has a <strong>per-order courier override</strong> (set from the Orders page), it wins.</li>
            <li>Otherwise rules are evaluated in <strong>priority order</strong> (lowest number first). First active rule whose amount range covers the order total is used.</li>
            <li>If no rule matches, Shiprocket assigns its default cheapest serviceable courier.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminCourierRules;
